import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getFormattedNumber } from "@msys/formatting";
import { Close as CloseIcon } from "@mui/icons-material";
import { KeyboardArrowDown as KeyboardArrowDown } from "@mui/icons-material";
import { Autocomplete, autocompleteClasses, Button, FilledInput, filledInputClasses, IconButton, Stack, Typography, } from "@mui/material";
import { styled } from "@mui/material/styles";
import { identity, isNil, sortBy } from "lodash-es";
import { matchSorter } from "match-sorter";
import React from "react";
import { useLocale } from "../LocaleProvider.js";
import { PopoverOpenButton, } from "../popover/PopoverOpenButton.js";
export const defaultFilterOptions = (options, { inputValue }) => {
    if (!inputValue)
        return options;
    const matchedOptions = matchSorter(options, inputValue, { keys: ["label"] });
    if (!isNil(options[0]?.counter)) {
        return matchedOptions.sort((o1, o2) => (o2.counter ?? 0) - (o1.counter ?? 0));
    }
    return matchedOptions;
};
export const ButtonSelect = ({ showCount = true, ...props }) => {
    const [open, setOpen] = React.useState(false);
    const popoverOpenButtonRef = React.useRef();
    return (_jsx(PopoverOpenButton, { onOpen: () => setOpen(true), onClose: () => setOpen(false), content: _jsx(ButtonSelectForm, { ...props, handleClose: () => popoverOpenButtonRef.current?.handleClose() }), anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
        }, transformOrigin: {
            vertical: "top",
            horizontal: "left",
        }, popoverRef: popoverOpenButtonRef, children: _jsxs(Button, { size: "extra-small", variant: "text", color: "primary", sx: { textTransform: "none" }, endIcon: _jsx(KeyboardArrowDown, { sx: {
                    transition: "transform 0.2s ease-out",
                    ...(open ? { transform: `rotate(-180deg)` } : undefined),
                } }), disabled: props.disabled, children: [props.label, showCount && props.value !== null ? " (1)" : ""] }) }));
};
const StyledAutocompletePopper = styled("div")(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: "none",
        border: "none",
        margin: 0,
        marginRight: "-16px",
        color: "inherit",
        borderRadius: 0,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: "auto",
            alignItems: "flex-start",
            borderRadius: 0,
            // '&[aria-selected="true"]': {
            //   backgroundColor: "transparent",
            // },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
                backgroundColor: theme.palette.action.hover,
            },
        },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: "relative",
    },
}));
export function PopperComponent(props) {
    const { disablePortal, anchorEl, open, ...other } = props;
    return _jsx(StyledAutocompletePopper, { ...other });
}
function ButtonSelectForm({ searchLabel, noOptionsLabel, options, value, onChange, clearable, filterOptions = defaultFilterOptions, handleClose, popoverWidth = "md", }) {
    const locale = useLocale();
    const [inputValue, setInputValue] = React.useState("");
    const _options = React.useMemo(() => sortBy(filterOptions(options, { inputValue }), o => o.value === value ? 0 : 1), [filterOptions, inputValue, options, value]);
    return (_jsx(Stack, { sx: theme => ({
            width: `${theme.layout.filters.popoverWidth[popoverWidth]}px`,
        }), spacing: 0, direction: "column", children: _jsx(Autocomplete, { open: true, fullWidth: true, size: "extra-small", onClose: (event, reason) => {
                if (reason === "escape") {
                    handleClose();
                }
            }, inputValue: inputValue, onInputChange: (_, value, reason) => {
                if (reason === "input") {
                    setInputValue(value);
                }
            }, value: _options.find(o => o.value === value) ?? null, onChange: (event, newValue, reason) => {
                if (event.type === "keydown" &&
                    (event.key === "Backspace" ||
                        event.key === "Delete") &&
                    reason === "removeOption") {
                    return;
                }
                onChange(newValue?.value ?? null);
                if (newValue?.value)
                    handleClose();
            }, filterOptions: identity, noOptionsText: noOptionsLabel, renderOption: (props, option, { selected }) => (_createElement("li", { ...props, key: typeof option.value === "string"
                    ? option.value
                    : JSON.stringify(option.value) }, option.icon || option.counter !== undefined || clearable ? (_jsxs(Stack, { alignItems: "center", direction: "row", spacing: 1, flex: 1, children: [option.icon, _jsx(Typography, { variant: "body2", sx: { flex: 1 }, children: option.label }), option.counter !== undefined ? (_jsx(Typography, { variant: "caption", sx: { color: "grey.600" }, children: getFormattedNumber(option.counter, locale) })) : null, clearable && option.value === value && (_jsx("div", { style: {
                            height: 0,
                            display: " flex",
                            alignItems: "center",
                        }, children: _jsx(IconButton, { size: "small", onClick: e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(null);
                            }, children: _jsx(CloseIcon, { fontSize: "small" }) }) }))] })) : (_jsx(Typography, { variant: "body2", children: option.label })))), options: _options, getOptionLabel: option => option.label, renderInput: params => (_jsx(FilledInput, { size: "extra-small", type: "search", fullWidth: true, ref: params.InputProps.ref, inputProps: { enterKeyHint: "search", ...params.inputProps }, autoFocus: true, placeholder: `${searchLabel}…`, sx: {
                    [`&.${filledInputClasses.root}`]: {
                        padding: "0 !important",
                    },
                } })), sx: theme => ({
                [`&.${autocompleteClasses.root}`]: {
                    paddingX: 1,
                    width: "100%",
                    ...(options.length < 10
                        ? { height: 0, overflow: "hidden" }
                        : {
                            paddingY: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }),
                },
            }), slots: {
                popper: PopperComponent,
            } }) }));
}
//# sourceMappingURL=ButtonSelect.js.map